/**
 * HCP Search Page
 * Main search interface for Healthcare Providers
 * 
 * ARCHITECTURAL STANDARDS:
 * - useHCPSearch hook for logic
 * - Modular components (Form, Table)
 * - Centralized API config
 * - Error handling with toast
 * - Responsive design
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Tabs, Tab, Grid, TextField, Button, Radio, Tooltip, MenuItem, Skeleton, IconButton, Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import {
  MRT_GlobalFilterTextField,
  MRT_FullScreenToggleButton as MrtFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MrtShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as MrtToggleDensePaddingButton,
  MRT_ToggleFiltersButton as MrtToggleFiltersButton,
  MRT_ToggleGlobalFilterButton as MrtToggleGlobalFilterButton,
} from 'material-react-table';
import useHCPSearch from './hooks/useHCPSearch';
import HCPSearchForm from './components/HCPSearchForm';
import HCPResultsTable from './components/HCPResultsTable';
import GlobalMaterialTable from '../../../components/common/GlobalMaterialTable';
import { commonTableStyles } from '../../../components/common/tableStyles';
import apiClient from '../../../services/apiClient';
import { API_CONFIG, buildEndpoint } from '../../../config/api.config';
import { buildHcpQuery, normalizeSolrArray } from '../../../utils/solrQuery';
import SearchIcon from '@mui/icons-material/Search';
import { ImSpinner2 } from 'react-icons/im';
import { FiDownload } from 'react-icons/fi';

const HCP_GLOBAL_SEARCH_STORAGE_KEY = 'hcpReactGlobalSearchState';
const HCP_SEARCH_UI_STORAGE_KEY = 'hcpReactSearchUiState';
const getSelectProps = (placeholder) => ({
  displayEmpty: true,
  renderValue: (selected) => selected || placeholder,
});

const getInputPlaceholder = (label) => `Enter ${label}`;

const SelectionRadioCell = ({ row, selectedRowId, onSelect }) => {
  const rowId = String(row.original.okIndvId || row.original.id || '');

  return (
    <Radio
      checked={rowId === String(selectedRowId)}
      onChange={() => onSelect(rowId)}
      size="small"
    />
  );
};

SelectionRadioCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      okIndvId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
  }).isRequired,
  selectedRowId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const renderTruncatedCell = (value, width = 150) => {
  const displayValue = value || '-';

  return (
    <Tooltip title={displayValue} placement="top" arrow>
      <Box
        component="span"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          maxWidth: width,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          wordBreak: 'normal',
          overflowWrap: 'normal',
          lineHeight: 1.2,
          verticalAlign: 'bottom',
        }}
      >
        {displayValue}
      </Box>
    </Tooltip>
  );
};

const HCPSearch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const initialUiState = React.useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem(HCP_SEARCH_UI_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }, []);
  const initialGlobalFilters = React.useMemo(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(HCP_GLOBAL_SEARCH_STORAGE_KEY) || '{}');
      return {
        firstName: '',
        lastName: '',
        fnameLname: '',
        okIndvId: '',
        specialty: '',
        profession: '',
        city: '',
        state: '',
        zip: '',
        businessName: '',
        npiId: '',
        status: '',
        ...(stored || {}),
      };
    } catch {
      return {
        firstName: '',
        lastName: '',
        fnameLname: '',
        okIndvId: '',
        specialty: '',
        profession: '',
        city: '',
        state: '',
        zip: '',
        businessName: '',
        npiId: '',
        status: '',
      };
    }
  }, []);
  const [mainTab, setMainTab] = React.useState(() => {
    if (typeParam === 'Global') return 1;
    if (typeParam === 'Advanced' || typeParam === 'Basic') return 0;
    return initialUiState?.mainTab === 1 ? 1 : 0;
  });
  const [globalFilters, setGlobalFilters] = React.useState(() => ({
    ...initialGlobalFilters,
    npiId: searchParams.get('npi') || initialGlobalFilters.npiId || '',
  }));
  const [globalResults, setGlobalResults] = React.useState([]);
  const [globalLoading, setGlobalLoading] = React.useState(false);
  const [localExportLoading, setLocalExportLoading] = React.useState(false);
  const [GlobalExportLoading, setGlobalExportLoading] = React.useState(false);
  const [selectedGlobalRowId, setSelectedGlobalRowId] = React.useState('');
  const [creatingAccount, setCreatingAccount] = React.useState(false);
  const initializedGlobalNpiSearchRef = React.useRef(false);

  const {
    loading,
    results,
    totalCount,
    filters: localFilters, // Renamed from filters
    facets,
    mode: localSearchMode, // Renamed from mode
    page,
    pageSize: limit, // Renamed from pageSize
    facetsLoading,
    autoSearch,
    search: handleLocalSearch, // Renamed from search
    updateFilters: setLocalFilters, // Renamed from updateFilters
    updateFiltersAndSearch: setLocalFiltersAndSearch,
    clearFilters: handleLocalClear, // Renamed from clearFilters
    setPage,
    setPageSize: setLimit, // Renamed from setPageSize
    setAutoSearch,
    setMode: setLocalSearchMode, // Renamed from setMode
  } = useHCPSearch();

  React.useEffect(() => {
    sessionStorage.setItem(HCP_GLOBAL_SEARCH_STORAGE_KEY, JSON.stringify(globalFilters));
  }, [globalFilters]);

  React.useEffect(() => {
    sessionStorage.setItem(HCP_SEARCH_UI_STORAGE_KEY, JSON.stringify({ mainTab }));
  }, [mainTab]);

  React.useEffect(() => {
    if (typeParam === 'Advanced') {
      setMainTab(0);
      setLocalSearchMode('advanced');
    } else if (typeParam === 'Basic') {
      setMainTab(0);
      setLocalSearchMode('basic');
    } else if (typeParam === 'Global') {
      setMainTab(1);
    }
  }, [setLocalSearchMode, typeParam]);

  React.useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (mainTab === 1) {
      nextParams.set('type', 'Global');
      // Intentionally not syncing npiId on keystroke
    } else {
      nextParams.set('type', localSearchMode === 'advanced' ? 'Advanced' : 'Basic');
      nextParams.delete('npi');
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [mainTab, localSearchMode, searchParams, setSearchParams]);

  /**
   * Handle row click - navigate to details
   */
  const handleRowClick = (hcpId) => {
    navigate(`/details/hcp/${hcpId}`);
  };

  const handleGlobalChange = (field) => (event) => {
    setGlobalFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const emptyGlobalFilters = {
    firstName: '',
    lastName: '',
    fnameLname: '',
    okIndvId: '',
    specialty: '',
    profession: '',
    city: '',
    state: '',
    zip: '',
    businessName: '',
    npiId: '',
    status: '',
  };

  const handleClearGlobalField = (field) => () => {
    setGlobalFilters((prev) => {
      const nextFilters = { ...prev, [field]: '' };
      handleGlobalSearch(nextFilters);
      return nextFilters;
    });
  };

  const handleClearAllGlobalFields = () => {
    setGlobalFilters(emptyGlobalFilters);
    handleGlobalSearch(emptyGlobalFilters);
  };

  const selectedGlobalRow = React.useMemo(
    () => globalResults.find((row) => String(row.okIndvId || row.id || '') === String(selectedGlobalRowId)) || null,
    [globalResults, selectedGlobalRowId]
  );
  const globalDisplayCount = React.useMemo(() => Number(globalResults.length || 0), [globalResults.length]);

  const handleGlobalSearch = React.useCallback(async (overrideFilters) => {
    // Make sure overrideFilters is a valid object, as onClick can pass an event object
    const filtersToUse = overrideFilters && typeof overrideFilters === 'object' && !overrideFilters.nativeEvent ? overrideFilters : globalFilters;

    setGlobalLoading(true);
    setSelectedGlobalRowId('');
    try {
      const response = await apiClient.post(API_CONFIG.hcp.onekeySearch.endpoint, { data: filtersToUse });
      const data = response?.data?.hcp || [];
      setGlobalResults(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Unable to run global search. Please try again.');
      setGlobalResults([]);
    } finally {
      setGlobalLoading(false);
    }
  }, [globalFilters]);

  const hasAutoSearchedOnTabSwitchRef = React.useRef(false);

  React.useEffect(() => {
    if (mainTab !== 1) {
      hasAutoSearchedOnTabSwitchRef.current = false;
      return;
    }

    if (!hasAutoSearchedOnTabSwitchRef.current) {
      hasAutoSearchedOnTabSwitchRef.current = true;

      let nextFilters = { ...globalFilters };

      // Override from URL if initial load for NPI
      if (searchParams.get('npi') && !initializedGlobalNpiSearchRef.current) {
        initializedGlobalNpiSearchRef.current = true;
        nextFilters.npiId = searchParams.get('npi') || '';
      }

      // Prefill from local filters
      nextFilters = {
        ...nextFilters,
        fnameLname: localFilters.basicText || nextFilters.fnameLname,
        profession: localFilters.professions || nextFilters.profession,
        specialty: localFilters.specialities || nextFilters.specialty,
        state: localFilters.state || nextFilters.state,
        zip: localFilters.zip5 || nextFilters.zip,
        npiId: localFilters.npiId || nextFilters.npiId,
      };

      setGlobalFilters(nextFilters);

      const hasAnyFilter = Object.values(nextFilters).some((v) => v !== '');
      if (hasAnyFilter) {
        handleGlobalSearch(nextFilters);
      }
    }
  }, [mainTab, localFilters, globalFilters, searchParams, handleGlobalSearch]);

  const handleCreateNewAccount = async () => {
    if (!selectedGlobalRow?.okIndvId) {
      toast.error('Please select the HCP');
      return;
    }

    setCreatingAccount(true);
    try {
      const endpoint = buildEndpoint(API_CONFIG.hcp.createNewAccount.endpoint, {
        okIndvId: selectedGlobalRow.okIndvId,
      });
      const response = await apiClient.post(endpoint);
      const mdmId = response?.data?.mdmId;

      toast.success('New account created successfully');

      if (mdmId) {
        navigate(`/details/hcp/${mdmId}`);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.errorReason
        || error?.response?.data?.status
        || error?.response?.data?.error
        || 'Unable to create the account. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCreatingAccount(false);
    }
  };

  const downloadCsv = (rows, columns, fileName) => {
    const header = columns.join(',');
    const csvRows = rows.map((row) => columns
      .map((key) => {
        const value = row?.[key] ?? '';
        const safe = String(value).replaceAll('"', '""');
        return `"${safe}"`;
      })
      .join(','));

    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const mapHcpDocToRow = (doc) => {
    const first = normalizeSolrArray(doc.fname);
    const middle = normalizeSolrArray(doc.mname);
    const last = normalizeSolrArray(doc.lname);

    return {
      id: doc.mdm_id || doc.ok_indv_id || doc.combo_id || doc.id,
      source: normalizeSolrArray(doc.source),
      mdmId: normalizeSolrArray(doc.mdm_id),
      firstName: first,
      middleName: middle,
      lastName: last,
      suffix: normalizeSolrArray(doc.sfx_cd),
      gender: normalizeSolrArray(doc.gender),
      isnId: normalizeSolrArray(doc.isn_id),
      status: normalizeSolrArray(doc.status),
      addressLine1: normalizeSolrArray(doc.addln1),
      city: normalizeSolrArray(doc.city),
      state: normalizeSolrArray(doc.state),
      zip: normalizeSolrArray(doc.zip5),
      businessName: normalizeSolrArray(doc.bus_name),
      professions: normalizeSolrArray(doc.professions),
      specialities: normalizeSolrArray(doc.specialities),
      degree: normalizeSolrArray(doc.degree),
      country: normalizeSolrArray(doc.cntry),
      okIndvId: normalizeSolrArray(doc.ok_indv_id),
      fullProfile: normalizeSolrArray(doc.is_golden_universe),
      amaPdrp: normalizeSolrArray(doc.ama_pdrp_ind),
      slnTypeName: normalizeSolrArray(doc.st_lic_typ_nm),
      createdDate: normalizeSolrArray(doc.cre_date),
      updatedDate: normalizeSolrArray(doc.vrfy_date),
      mdmCreatedTime: normalizeSolrArray(doc.mdm_created_time),
      mdmUpdatedTime: normalizeSolrArray(doc.mdm_updated_time),
    };
  };

  const handleLocalExport = async () => {
    setLocalExportLoading(true);
    try {
      const rowsToFetch = Math.max(totalCount, 1);
      const q = buildHcpQuery(localFilters, 1, rowsToFetch, localSearchMode);
      const response = await apiClient.post(API_CONFIG.hcp.search.endpoint, { q });
      const docs = response?.data?.response?.docs || [];
      const exportRows = docs.map(mapHcpDocToRow);

      const columns = [
        'source', 'mdmId', 'firstName', 'middleName', 'lastName', 'suffix', 'gender', 'isnId', 'status',
        'addressLine1', 'city', 'state', 'zip', 'businessName', 'professions', 'specialities', 'degree',
        'country', 'okIndvId', 'fullProfile', 'amaPdrp', 'slnTypeName', 'createdDate', 'updatedDate',
        'mdmCreatedTime', 'mdmUpdatedTime',
      ];

      downloadCsv(exportRows, columns, 'hcp-search-results.csv');
    } catch {
      toast.error('Unable to export local search results. Please try again.');
    } finally {
      setLocalExportLoading(false);
    }
  };

  const handleGlobalExport = () => {
    setGlobalExportLoading(true);
    const columns = [
      'okIndvId', 'firstName', 'middleName', 'lastName', 'addr1', 'addr2', 'city', 'state', 'zip',
      'status', 'specialties', 'profession', 'isInContract',
    ];

    const exportRows = globalResults.map((row) => ({
      okIndvId: row.okIndvId || '-',
      firstName: row.firstName || '-',
      middleName: row.middleName || '-',
      lastName: row.lastName || '-',
      addr1: row.primaryAddress?.addrLn1 || '-',
      addr2: row.primaryAddress?.addrLn2 || '-',
      city: row.primaryAddress?.cityNm || '-',
      state: row.primaryAddress?.stCd || '-',
      zip: Array.isArray(row.primaryAddress?.zipCd) ? row.primaryAddress.zipCd.join('-') : (row.primaryAddress?.zipCd || '-'),
      status: row.status || '-',
      specialties: row.primarySpecialty || '-',
      profession: row.primaryProfession || '-',
      isInContract: row.isInContract || '-',
    }));

    downloadCsv(exportRows, columns, 'hcp-global-search-results.csv');
    setGlobalExportLoading(false);
  };

  const globalColumns = React.useMemo(() => ([
    {
      header: '',
      id: 'select',
      enableSorting: false,
      enableColumnActions: false,
      size: 48,
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => (
        <SelectionRadioCell row={row} selectedRowId={selectedGlobalRowId} onSelect={setSelectedGlobalRowId} />
      ),
    },
    {
      header: 'Ok Individual Id', id: 'okIndvId', accessorFn: (row) => row.okIndvId || '-', size: 150,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 150),
    },
    {
      header: 'First Name', id: 'firstName', accessorFn: (row) => row.firstName || '-', size: 120,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 120),
    },
    {
      header: 'Middle Name', id: 'middleName', accessorFn: (row) => row.middleName || '-', size: 120,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 120),
    },
    {
      header: 'Last Name', id: 'lastName', accessorFn: (row) => row.lastName || '-', size: 120,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 120),
    },
    {
      header: 'Address Line 1', id: 'addr1', accessorFn: (row) => row.primaryAddress?.addrLn1 || '-', size: 180,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 180),
    },
    {
      header: 'Address Line 2', id: 'addr2', accessorFn: (row) => row.primaryAddress?.addrLn2 || '-', size: 180,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 180),
    },
    {
      header: 'City', id: 'city', accessorFn: (row) => row.primaryAddress?.cityNm || '-', size: 130,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 130),
    },
    {
      header: 'State', id: 'state', accessorFn: (row) => row.primaryAddress?.stCd || '-', size: 90,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 90),
    },
    {
      header: 'Zip',
      id: 'zip',
      accessorFn: (row) =>
        Array.isArray(row.primaryAddress?.zipCd)
          ? row.primaryAddress.zipCd.join('-')
          : (row.primaryAddress?.zipCd || '-'),
      size: 110,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 110),
    },
    {
      header: 'Status', id: 'status', accessorFn: (row) => row.status || '-', size: 100,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 100),
    },
    {
      header: 'Specialties', id: 'specialties', accessorFn: (row) => row.primarySpecialty || '-', size: 160,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 160),
    },
    {
      header: 'Profession', id: 'profession', accessorFn: (row) => row.primaryProfession || '-', size: 140,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 140),
    },
    {
      header: 'Is In Contract', id: 'isInContract', accessorFn: (row) => row.isInContract || '-', size: 120,
      Cell: ({ cell }) => renderTruncatedCell(cell.getValue(), 120),
    },
  ]), [selectedGlobalRowId]);

  const softTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '4px',
      height: '38px',
      transition: 'all 0.15s ease',
      '& fieldset': {
        borderColor: '#e2e8f0',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2196f3',
        borderWidth: '1px',
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(33, 150, 243, 0.06)',
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
      
      color: '#0f172a',
      height: '32px',
      boxSizing: 'border-box',
      '&::placeholder': {
        
      }
    },
    '& .MuiInputLabel-root': {
      
      color: '#64748b',
    },
    '& .MuiSelect-select': {
      padding: '0 10px !important',
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      
      color: '#0f172a',
      boxSizing: 'border-box',
    }
  };

  const FieldLabel = ({ children }) => (
    <Typography sx={{ fontWeight: 600, color: '#475569', mb: 0.4, ml: 0.5,  lineHeight: 1.2, display: 'block' }}>
      {children}
    </Typography>
  );

  const renderHeader = () => (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      justifyContent: 'space-between',
      gap: 1.5,
      pb: mainTab === 1 ? 1.5 : 0.75,
      mb: mainTab === 1 ? 1.5 : 0,
      borderBottom: mainTab === 1 ? '1px solid #e2e8f0' : 'none',
    }}>
      <Typography sx={{  fontWeight: 600, color: '#0f172a', ml: 0.5, fontSize: theme.typography.h4.fontSize }}>
        HCP Search
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: "#f1f5f9",
          borderRadius: "8px",
          padding: "4px",
          width: "fit-content",
        }}
      >
        <Box
          onClick={() => {
            if (mainTab !== 0) {
              setMainTab(0);
              handleLocalClear();
            }
          }}
          sx={{
            minWidth: 120,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: "6px",
            
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: mainTab === 0 ? "#1e88e5" : "#64748b",
            backgroundColor: mainTab === 0 ? "#ffffff" : "transparent",
            boxShadow: mainTab === 0 ? "0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)" : "none",
            "&:hover": {
              color: mainTab === 0 ? "#1e88e5" : "#334155",
            }
          }}
        >
          Local Search
        </Box>

        <Box
          onClick={() => {
            if (mainTab !== 1) {
              setMainTab(1);
              setGlobalFilters({
                firstName: '',
                lastName: '',
                fnameLname: '',
                okIndvId: '',
                specialty: '',
                profession: '',
                city: '',
                state: '',
                zip: '',
                businessName: '',
                npiId: '',
                status: '',
              });
            }
          }}
          sx={{
            minWidth: 120,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: "6px",
            
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: mainTab === 1 ? "#1e88e5" : "#64748b",
            backgroundColor: mainTab === 1 ? "#ffffff" : "transparent",
            boxShadow: mainTab === 1 ? "0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)" : "none",
            "&:hover": {
              color: mainTab === 1 ? "#1e88e5" : "#334155",
            }
          }}
        >
          Global Search
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', pb: 2 }}>
      {/* Content */}
      <Box sx={{ width: '100%' }}>
        {mainTab === 0 && (
          <>
            <Paper elevation={0} sx={{ p: 1.5, paddingTop: "10px !important", mb: 1, borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              {renderHeader()}
              <HCPSearchForm
                filters={localFilters}
                facets={facets}
                mode={localSearchMode}
                onModeChange={setLocalSearchMode}
                onUpdateFilters={setLocalFilters}
                onUpdateFiltersAndSearch={setLocalFiltersAndSearch}
                onSearch={handleLocalSearch}
                onClear={handleLocalClear}
                autoSearch={autoSearch}
                onAutoSearchChange={setAutoSearch}
                loading={loading || facetsLoading}
              />
            </Paper>

            <Box sx={{ mt: 1.5 }}>
              <HCPResultsTable
                results={results}
                totalCount={totalCount}
                pagination={{ page, limit }}
                onPaginationChange={(newPage, newLimit) => {
                  setPage(newPage);
                  setLimit(newLimit);
                }}
                onExport={handleLocalExport}
                exportLoading={localExportLoading}
                loading={loading || facetsLoading}
                onRowClick={handleRowClick}
              />
            </Box>
          </>
        )}

        {mainTab === 1 && (
          <>
            <Paper elevation={0} sx={{ p: 1.5, mb: 1, borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              {renderHeader()}
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>First Name</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('First Name')} value={globalFilters.firstName} onChange={handleGlobalChange('firstName')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Last Name</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('Last Name')} value={globalFilters.lastName} onChange={handleGlobalChange('lastName')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>First & Last Name</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('First & Last Name')} value={globalFilters.fnameLname} onChange={handleGlobalChange('fnameLname')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Ok Individual Id</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('Ok Individual Id')} value={globalFilters.okIndvId} onChange={handleGlobalChange('okIndvId')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Specialty</FieldLabel>
                  <TextField
                    sx={softTextFieldSx}
                    fullWidth
                    size="small"
                    select
                    value={globalFilters.specialty}
                    onChange={handleGlobalChange('specialty')}
                    {...getSelectProps('Select Specialty')}
                  >
                    <MenuItem value="">All Specialties</MenuItem>
                    {(facets?.specialities || []).map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Profession</FieldLabel>
                  <TextField
                    sx={softTextFieldSx}
                    fullWidth
                    size="small"
                    select
                    value={globalFilters.profession}
                    onChange={handleGlobalChange('profession')}
                    {...getSelectProps('Select Profession')}
                  >
                    <MenuItem value="">All Professions</MenuItem>
                    {(facets?.professions || []).map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>City</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('City')} value={globalFilters.city} onChange={handleGlobalChange('city')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>State</FieldLabel>
                  <TextField
                    sx={softTextFieldSx}
                    fullWidth
                    size="small"
                    select
                    value={globalFilters.state}
                    onChange={handleGlobalChange('state')}
                    {...getSelectProps('Select State')}
                  >
                    <MenuItem value="">All States</MenuItem>
                    {(facets?.state || []).map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Zip Code</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('Zip')} value={globalFilters.zip} onChange={handleGlobalChange('zip')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Business Name</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('Business Name')} value={globalFilters.businessName} onChange={handleGlobalChange('businessName')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>NPI ID</FieldLabel>
                  <TextField sx={softTextFieldSx} fullWidth size="small" placeholder={getInputPlaceholder('NPI Id')} value={globalFilters.npiId} onChange={handleGlobalChange('npiId')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FieldLabel>Status</FieldLabel>
                  <TextField
                    sx={softTextFieldSx}
                    fullWidth
                    size="small"
                    select
                    value={globalFilters.status}
                    onChange={handleGlobalChange('status')}
                    {...getSelectProps('Select Status')}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {(facets?.status || []).map((item) => (
                      <MenuItem key={item} value={item}>{item}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 0 }}>
                  <Button className="action-btn process-btn"
                    variant="contained"
                    onClick={handleGlobalSearch}
                    disabled={globalLoading}
                    startIcon={<SearchIcon />}
                    disableElevation
                    sx={{
                      minWidth: 160,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 700,
                      height: 38,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                      '&.Mui-disabled': {
                        backgroundColor: '#e2e8f0',
                        color: '#94a3b8'
                      }
                    }}
                  >
                    {globalLoading ? 'Searching...' : 'Search'}
                  </Button>
                </Grid>
              </Grid>

              {(() => {
                const hasGlobalActiveFilters = Boolean(
                  globalFilters.firstName ||
                  globalFilters.lastName ||
                  globalFilters.fnameLname ||
                  globalFilters.okIndvId ||
                  globalFilters.specialty ||
                  globalFilters.profession ||
                  globalFilters.city ||
                  globalFilters.state ||
                  globalFilters.zip ||
                  globalFilters.businessName ||
                  globalFilters.npiId ||
                  globalFilters.status
                );

                const getChipProps = (label, field) => ({
                  label,
                  size: 'small',
                  onDelete: handleClearGlobalField(field),
                  deleteIcon: (
                    <CloseIcon
                      sx={{
                        fontSize: "15px !important",
                        color: "#64748b",
                        transition: "0.2s",
                        mr: 1,
                        cursor: "pointer",
                        "&:hover": { color: "#ef4444" },
                      }}
                    />
                  ),
                  sx: { backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', fontWeight: 500, height: 24,  }
                });

                return hasGlobalActiveFilters ? (
                  <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', pt: 2, borderTop: '1px solid #e2e8f0' }}>
                    <FieldLabel>Active Selection</FieldLabel>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                      {globalFilters.firstName && <Chip {...getChipProps(`First Name: ${globalFilters.firstName}`, 'firstName')} />}
                      {globalFilters.lastName && <Chip {...getChipProps(`Last Name: ${globalFilters.lastName}`, 'lastName')} />}
                      {globalFilters.fnameLname && <Chip {...getChipProps(`Name: ${globalFilters.fnameLname}`, 'fnameLname')} />}
                      {globalFilters.okIndvId && <Chip {...getChipProps(`Ok Indv Id: ${globalFilters.okIndvId}`, 'okIndvId')} />}
                      {globalFilters.specialty && <Chip {...getChipProps(`Specialty: ${globalFilters.specialty}`, 'specialty')} />}
                      {globalFilters.profession && <Chip {...getChipProps(`Profession: ${globalFilters.profession}`, 'profession')} />}
                      {globalFilters.city && <Chip {...getChipProps(`City: ${globalFilters.city}`, 'city')} />}
                      {globalFilters.state && <Chip {...getChipProps(`State: ${globalFilters.state}`, 'state')} />}
                      {globalFilters.zip && <Chip {...getChipProps(`Zip: ${globalFilters.zip}`, 'zip')} />}
                      {globalFilters.businessName && <Chip {...getChipProps(`Business: ${globalFilters.businessName}`, 'businessName')} />}
                      {globalFilters.npiId && <Chip {...getChipProps(`NPI: ${globalFilters.npiId}`, 'npiId')} />}
                      {globalFilters.status && <Chip {...getChipProps(`Status: ${globalFilters.status}`, 'status')} />}

                      <Button className="action-btn cancel-btn"
                        variant="text"
                        size="small"
                        onClick={handleClearAllGlobalFields}
                        disabled={globalLoading}
                        sx={{ textTransform: 'none', fontWeight: 600, color: theme.palette.primary.main,  minWidth: 'auto', p: 0, ml: 1 }}
                      >
                        Reset All
                      </Button>
                    </Box>
                  </Box>
                ) : null;
              })()}
            </Paper>
            <Paper elevation={0} sx={{ p: 0, borderRadius: '16px', border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
              <GlobalMaterialTable
                columns={globalColumns}
                data={globalResults}
                isLoading={globalLoading}
                renderTopToolbarCustomActions={() => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                      }}
                    >
                      {/* <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#1e88e5",
                        boxShadow: "0 0 0 4px rgba(30,136,229,0.12)",
                      }}
                    /> */}

                      <Typography
                        sx={{
                          
                          fontWeight: 700,
                          color: "#0f172a",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        Results ({globalDisplayCount})
                      </Typography>

                      {/* <Box
                        sx={{
                          height: 26,
                          minWidth: 42,
                          px: 1.2,
                          borderRadius: "8px",
                          background:
                            "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
                          border: "1px solid #dbe3ec",

                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",

                          color: "#334155",
                          
                          fontWeight: 800,

                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                        }}
                      >
                        {globalDisplayCount}
                      </Box> */}
                    </Box>
                  </Box>
                )}
                renderToolbarInternalActions={({ table }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MRT_GlobalFilterTextField table={table} />
                    <MrtToggleGlobalFilterButton table={table} />
                    <MrtToggleFiltersButton table={table} />
                    <MrtShowHideColumnsButton table={table} />
                    <MrtToggleDensePaddingButton table={table} />
                    <MrtFullScreenToggleButton table={table} />
                    <Tooltip title="Export">
                      <IconButton
                        onClick={handleGlobalExport}
                        disabled={GlobalExportLoading || globalResults.length === 0}
                        sx={{
                          color: '#475569',

                          '&.Mui-disabled': {
                            color: '#94a3b8',
                            cursor: 'not-allowed',
                            pointerEvents: 'auto',
                          }
                        }}
                      >
                        {GlobalExportLoading ? (
                          <ImSpinner2 size={15} className="spinner-icon" />
                        ) : (
                          <FiDownload size={18} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Button className="action-btn process-btn"
                      variant="contained"
                      size="small"
                      onClick={handleCreateNewAccount}
                      disabled={creatingAccount || !selectedGlobalRow?.okIndvId}
                      disableElevation
                      sx={{
                        borderRadius: '6px',
                        textTransform: 'none',
                        marginLeft: 1,
                        minHeight: 36,
                        fontWeight: 600,
                        
                        '&.Mui-disabled': {
                          backgroundColor: "#e2e8f0",
                          color: "#94a3b8",
                          cursor: 'not-allowed',
                          pointerEvents: 'auto',
                        }
                      }}
                    >
                      {creatingAccount ? 'Processing...' : 'Create New Account'}
                    </Button>
                  </Box>
                )}
                muiSearchTextFieldProps={{
                  placeholder: 'Search global results...',
                  variant: 'outlined',
                  size: 'small',
                  sx: {
                    minWidth: '300px',
                    '& .MuiInputBase-root': {
                      mr: 1,
                      mt: 0.4,
                    },
                    '& .MuiInputAdornment-root': { mt: '0 !important' },
                    '& .MuiSvgIcon-root': { mt: '0 !important' }
                  }
                }}
                muiTopToolbarProps={{
                  sx: {
                    '& .MuiToolbar-root': {
                      px: 0,
                      py: 0,
                      minHeight: 'unset',
                      alignItems: 'center',
                    }
                  }
                }}
                emptyMessage="No providers found. Try changing or clearing filters."
                enablePagination
                enableBottomToolbar
                getRowId={(row, index) => String(row.okIndvId || row.id || index)}
                onRowClick={(row) => setSelectedGlobalRowId(String(row.okIndvId || row.id || ''))}
                selectedRowId={selectedGlobalRowId}
                initialState={{ pagination: { pageIndex: 0, pageSize: 10 } }}
                enableStickyHeader
              />
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default HCPSearch;
